import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon } from 'tdesign-icons-react';
import { bannerApi } from '../../api';
import type { Banner, BannerAddDTO } from '../../types/banner';
import CoverImageUploader from '../../components/CoverImageUploader';

const typeOptions = [
  { label: '首页轮播', value: 'brand' },
  { label: '商城轮播', value: 'mall' },
];

export default function BannerManage() {
  const [data, setData] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [searchForm] = Form.useForm();

  const formRef = useRef<any>(null);

  const columns = [
    // { colKey: 'id', title: 'ID', width: 80 },
    {
      colKey: 'image',
      title: '图片',
      width: 150,
      cell: ({ row }: any) => (
        <img
          src={row.image}
          alt="轮播图"
          className="w-20 h-12 object-cover rounded"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ),
    },
    { colKey: 'title', title: '标题', ellipsis: true },
    // { colKey: 'targetUrl', title: '跳转链接', ellipsis: true },
    {
      colKey: 'type',
      title: '类型',
      width: 120,
      cell: ({ row }: any) => {
        const typeMap: Record<string, { label: string; theme: string }> = {
          'brand': { label: '首页轮播', theme: 'primary' },
          'mall': { label: '商城轮播', theme: 'success' },
        };
        const t = typeMap[row.type] || { label: '未知', theme: 'default' };
        return <Tag theme={t.theme as any}>{t.label}</Tag>;
      },
    },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => (
        <Switch value={row.status === 1} onChange={(val) => handleStatusChange(row, val)} />
      ),
    },
    { colKey: 'sortOrder', title: '排序', width: 80 },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    { colKey: 'updateTime', title: '更新时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          <Button theme="success" variant="text" icon={<EditIcon />} onClick={() => handleEdit(row.id)}>编辑</Button>
          <Popconfirm content="确定删除该轮播图？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  
  interface FetchParams {
    pageNum?: number;
    pageSize?: number;
  }
  const fetchData = async (params : FetchParams = {}) => {
    setLoading(true);
    try {
        const rawSearchParams  = searchForm.getFieldsValue?.(true) || {};
      // 清洗参数：移除 undefined, null 和空字符串
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      const res = await bannerApi.getPageList({ 
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          ...pagination,
          total: res.data.total,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetFormState = () => {
    setImageUrl('');
    setEditingBanner(null);
    if (formRef.current) {
      formRef.current.reset();
    }
  };
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };

  const handleAdd = () => {
    setEditingBanner(null);
    setImageUrl('');
    if (formRef.current) {
      formRef.current.reset();
    }
    setDialogVisible(true);
  };

  const handleEdit = async (id: number) => {
    try {
      const res = await bannerApi.getById(id);
      if (res.code === 200) {
        const data = res.data;
        data.status = data.status === 1 ? true : false;
        setEditingBanner(data);
        setImageUrl(data.image);
        formRef.current?.setFieldsValue(data);
        setDialogVisible(true);
      } else {
        MessagePlugin.error(res.message || '获取详情失败');
      }
    } catch {
      MessagePlugin.error('获取详情失败');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!imageUrl) {
        MessagePlugin.error('请上传图片');
        return;
      }

      const result = await formRef.current?.validate();
      if (result !== true) {
        return;
      }

      const values = formRef.current?.getFieldsValue(true) || {};
      const submitData: BannerAddDTO = {
        ...values,
        image: imageUrl,
        status: values.status ? 1 : 0,
        sortOrder: values.sortOrder ? Number(values.sortOrder) : 0,
      };

      const res = editingBanner
        ? await bannerApi.update(editingBanner.id, submitData)
        : await bannerApi.create(submitData);

      if (res.code === 200) {
        MessagePlugin.success(editingBanner ? '更新成功' : '创建成功');
        setDialogVisible(false);
        resetFormState();
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      console.error('提交失败：', err);
      MessagePlugin.error('表单验证失败或服务器异常');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await bannerApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const handleStatusChange = async (banner: Banner, enabled: boolean) => {
    try {
      const res = await bannerApi.update(banner.id, { status: enabled ? 1 : 0 });
      if (res.code === 200) {
        MessagePlugin.success(enabled ? '已启用' : '已禁用');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch {
      MessagePlugin.error('操作失败');
    }
  };

  return (
    <div className="p-4">
        <div className="flex justify-between items-center mb-6">
            <h1 className="text-xl font-bold">轮播图管理</h1>
            <Button icon={<AddIcon />} theme="primary" onClick={handleAdd}>新增轮播图</Button>
        </div>
    
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
            <Form form={searchForm} layout="inline">
                <Form.FormItem name="status" label="状态">
                    <Select placeholder="请选择状态" clearable style={{ width: 120 }}>
                        <Select.Option value={1}>启用</Select.Option>
                        <Select.Option value={0}>禁用</Select.Option>
                    </Select>
                </Form.FormItem>
                <Form.FormItem name="type" label="类型">
                    <Select placeholder="请选择类型" clearable style={{ width: 120 }}>
                        <Select.Option value="brand">首页轮播</Select.Option>
                        <Select.Option value="mall">商城轮播</Select.Option>
                    </Select>
                </Form.FormItem>
                <Form.FormItem>
                    <Button theme="primary" onClick={handleSearch}>查询</Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>重置</Button>
                </Form.FormItem>
            </Form>
        </div>

      <Table
        columns={columns}
        data={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (pageInfo: { current: number; pageSize: number }) => {
            const { current, pageSize } = pageInfo;
            setPagination({ current, pageSize, total: pagination.total });
            fetchData({ pageNum: current, pageSize });
          },
        }}
        rowKey="id"
      />

      <Dialog
        header={editingBanner ? '编辑轮播图' : '新增轮播图'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false);
          resetFormState();
        }}
        onConfirm={handleSubmit}
        confirmBtn="保存"
        cancelBtn="取消"
        width={600}
        key={editingBanner ? `edit-${editingBanner.id}` : 'add-new'}
      >
        <Form
          ref={formRef}
          initialData={editingBanner || {}}
          labelWidth={100}
        >
          <Form.FormItem label="标题" name="title">
            <Input placeholder="请输入轮播图标题" />
          </Form.FormItem>

          <Form.FormItem label="图片" name="image" rules={[{ required: true, message: '请上传图片' }]}>
            <CoverImageUploader
              value={imageUrl}
              onChange={(value) => setImageUrl(Array.isArray(value) ? value[0] : value)}
              simple
            />
          </Form.FormItem>

          {/* <Form.FormItem label="跳转链接" name="targetUrl">
            <Input placeholder="请输入跳转链接（可选）" />
          </Form.FormItem> */}

          <Form.FormItem label="类型" name="type" rules={[{ required: true, message: '请选择类型' }]}>
            <Select options={typeOptions} placeholder="请选择轮播图类型" />
          </Form.FormItem>

          <Form.FormItem label="状态" name="status" initialValue={true}>
            <Switch />
          </Form.FormItem>

          <Form.FormItem label="排序" name="sortOrder">
            <Input type="number" placeholder="请输入排序值" />
          </Form.FormItem>  
        </Form>
      </Dialog>
    </div>
  );
}
